/**
 * Shortcode Parser
 *
 * Parses shortcode syntax from HTML content and extracts structured data
 * for rendering as React components.
 *
 * Supported shortcodes:
 * - [carousel images="url1,url2,url3" autoplay="true" interval="5000" ...]
 *
 * @module shortcode-parser
 */

export type ShortcodeType = "carousel";

export interface CarouselShortcodeData {
  type: "carousel";
  images: string[];
  autoplay: boolean;
  interval: number;
  showDots: boolean;
  showArrows: boolean;
  aspectRatio: "video" | "wide" | "square" | "hero" | "auto";
  height?: string;
  width?: string;
  fit: "cover" | "contain";
  slidesPerView: number;
  gap: number;
  loop: boolean;
  /** Focus mode: center slide is larger, side slides are smaller with smooth transitions */
  focus: boolean;
}

export type ShortcodeData = CarouselShortcodeData;

export interface ParsedShortcode {
  id: string;
  raw: string;
  data: ShortcodeData;
}

/**
 * Parses attribute string into key-value pairs.
 * Handles quoted values with spaces.
 */
function parseAttributes(attrString: string): Record<string, string> {
  const attrs: Record<string, string> = {};

  // Match attribute="value" or attribute='value' patterns
  const attrRegex = /(\w+)\s*=\s*(?:"([^"]*)"|'([^']*)')/g;
  let match;

  while ((match = attrRegex.exec(attrString)) !== null) {
    const key = match[1];
    const value = match[2] ?? match[3] ?? "";
    attrs[key] = value;
  }

  return attrs;
}

/**
 * Validates and sanitizes image URLs.
 * Only allows http, https, and relative paths.
 */
function sanitizeImageUrl(url: string): string | null {
  const trimmed = url.trim();

  // Allow relative paths
  if (trimmed.startsWith("/")) {
    return trimmed;
  }

  // Allow http and https URLs
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol === "http:" || parsed.protocol === "https:") {
      return trimmed;
    }
  } catch {
    // Invalid URL
  }

  return null;
}

/**
 * Validates and sanitizes CSS dimension values.
 * Only allows safe units: px, %, em, rem, vh, vw
 */
function sanitizeDimension(value: string | undefined): string | undefined {
  if (!value) return undefined;

  const trimmed = value.trim();

  // Allow: 300px, 50%, 20em, 10rem, 50vh, 50vw, auto
  if (/^(\d+(\.\d+)?(px|%|em|rem|vh|vw)|auto)$/i.test(trimmed)) {
    return trimmed;
  }

  // If just a number, assume pixels
  if (/^\d+(\.\d+)?$/.test(trimmed)) {
    return `${trimmed}px`;
  }

  return undefined;
}

/**
 * Parses a carousel shortcode and returns structured data.
 */
function parseCarouselShortcode(attrString: string): CarouselShortcodeData | null {
  const attrs = parseAttributes(attrString);

  // Images are required
  if (!attrs.images) {
    return null;
  }

  // Parse and validate image URLs
  const rawImages = attrs.images.split(",").map((s) => s.trim());
  const images = rawImages
    .map(sanitizeImageUrl)
    .filter((url): url is string => url !== null);

  if (images.length === 0) {
    return null;
  }

  // Parse optional attributes with defaults
  const autoplay = attrs.autoplay?.toLowerCase() === "true";
  const interval = Math.max(1000, Math.min(30000, parseInt(attrs.interval || "5000", 10) || 5000));
  const showDots = attrs.dots?.toLowerCase() !== "false";
  const showArrows = attrs.arrows?.toLowerCase() !== "false";

  // Parse dimensions
  const height = sanitizeDimension(attrs.height);
  const width = sanitizeDimension(attrs.width);

  // Parse fit mode - default to contain if height is specified, otherwise cover
  let fit: "cover" | "contain" = height ? "contain" : "cover";
  if (attrs.fit?.toLowerCase() === "cover") fit = "cover";
  if (attrs.fit?.toLowerCase() === "contain") fit = "contain";

  // Parse slider options
  const slidesPerView = Math.max(1, Math.min(10, parseInt(attrs.slides || attrs.visible || "1", 10) || 1));
  // Gap can be specified with or without "px" suffix
  const gapValue = attrs.gap ? parseInt(attrs.gap.replace(/px$/i, ""), 10) : 16;
  const gap = Math.max(0, Math.min(100, gapValue || 16));
  // Loop defaults to true for better UX
  const loop = attrs.loop?.toLowerCase() !== "false";
  // Focus mode: center slide is larger, side slides are smaller
  const focus = attrs.focus?.toLowerCase() === "true";

  // Parse aspect ratio - "auto" when height is set
  let aspectRatio: "video" | "wide" | "square" | "hero" | "auto" = height ? "auto" : "video";
  if (attrs.aspect) {
    const aspect = attrs.aspect.toLowerCase();
    if (aspect === "wide" || aspect === "square" || aspect === "hero" || aspect === "video" || aspect === "auto") {
      aspectRatio = aspect;
    }
  }

  return {
    type: "carousel",
    images,
    autoplay,
    interval,
    showDots,
    showArrows,
    aspectRatio,
    height,
    width,
    fit,
    slidesPerView,
    gap,
    loop,
    focus,
  };
}

/**
 * Parses content and replaces shortcodes with placeholder elements.
 * Returns the modified HTML.
 *
 * This approach preserves the HTML structure by inserting inline placeholders
 * rather than splitting the HTML into segments.
 *
 * Shortcode configuration is stored directly in the placeholder's data attributes,
 * ensuring server and client rendering produce identical HTML and eliminating
 * the need to match IDs between a Map and the DOM.
 */
export function replaceShortcodesWithPlaceholders(content: string): {
  html: string;
} {
  let shortcodeIndex = 0;

  // Match [shortcode ...] pattern
  const shortcodeRegex = /\[(\w+)([^\]]*)\]/g;

  const html = content.replace(shortcodeRegex, (raw, type, attrString) => {
    let data: ShortcodeData | null = null;

    switch (type.toLowerCase()) {
      case "carousel":
        data = parseCarouselShortcode(attrString);
        break;
    }

    if (data) {
      const id = `sc-${shortcodeIndex++}`;
      // URL-encode the JSON config - works identically on server and client
      const configEncoded = encodeURIComponent(JSON.stringify(data));

      // Return a placeholder div with embedded config (div ensures proper block layout)
      return `<div data-shortcode-type="${data.type}" data-shortcode-id="${id}" data-shortcode-config="${configEncoded}" class="shortcode-placeholder" style="display:block; width:100%;"></div>`;
    }

    // If shortcode parsing failed, remove it from output
    return "";
  });

  return { html };
}

/**
 * Checks if content contains any shortcodes.
 */
export function hasShortcodes(content: string): boolean {
  // Match [carousel followed by space or ] (for shortcodes with or without attributes)
  return /\[carousel[\s\]]/i.test(content);
}

// Legacy exports for backwards compatibility
export interface ContentSegment {
  type: "html" | "shortcode";
  content: string | ShortcodeData;
}

export interface ParsedShortcodeOld {
  raw: string;
  data: ShortcodeData;
  startIndex: number;
  endIndex: number;
}

export function findShortcodes(content: string): ParsedShortcodeOld[] {
  const shortcodes: ParsedShortcodeOld[] = [];
  const shortcodeRegex = /\[(\w+)([^\]]*)\]/g;
  let match;

  while ((match = shortcodeRegex.exec(content)) !== null) {
    const [raw, type, attrString] = match;
    const startIndex = match.index;
    const endIndex = startIndex + raw.length;

    let data: ShortcodeData | null = null;

    switch (type.toLowerCase()) {
      case "carousel":
        data = parseCarouselShortcode(attrString);
        break;
    }

    if (data) {
      shortcodes.push({ raw, data, startIndex, endIndex });
    }
  }

  return shortcodes;
}

export function parseContentWithShortcodes(content: string): ContentSegment[] {
  const shortcodes = findShortcodes(content);

  if (shortcodes.length === 0) {
    return [{ type: "html", content }];
  }

  const segments: ContentSegment[] = [];
  let lastIndex = 0;

  for (const shortcode of shortcodes) {
    if (shortcode.startIndex > lastIndex) {
      const htmlContent = content.slice(lastIndex, shortcode.startIndex);
      if (htmlContent.trim()) {
        segments.push({ type: "html", content: htmlContent });
      }
    }
    segments.push({ type: "shortcode", content: shortcode.data });
    lastIndex = shortcode.endIndex;
  }

  if (lastIndex < content.length) {
    const htmlContent = content.slice(lastIndex);
    if (htmlContent.trim()) {
      segments.push({ type: "html", content: htmlContent });
    }
  }

  return segments;
}
